/**
 * @fileoverview Settings: five sections, one card each, in the order an
 * instructor is likely to want them.
 *
 *   Profile        — who they are, and whether organizations may find them
 *   Plan & billing — the plan, recording minutes, students, payouts
 *   Share Hans     — refer a teacher (+ founding renewal), family seats
 *   Notifications  — the three email switches
 *   Account        — privacy, resources, sign out
 *
 * It was thirteen stand-alone tiles, each with its own eyebrow and border.
 * Two things about the consolidation are decisions rather than layout.
 *
 * **The plan is named from the EFFECTIVE tier, everywhere on this page.**
 * `/subscription/status` resolves it (the X7 pin applied, org coverage folded
 * in), and `/auth/me` carries the same answer as `effective_tier` for the
 * first paint. Reading `subscription_tier` instead is how a Founding Ensemble
 * member came to read "Solo" with an Upgrade button while the badge beside
 * their name said Studio AI.
 *
 * **Organization membership is not here.** The Programs page is where a
 * membership is seen, joined and left — including "Leave organization", the
 * consent-withdrawal path from Spec 11 — so Settings no longer duplicates it.
 * The one org surface that stays is the pending-invite banner, because an
 * invitation has to be answerable from somewhere the instructor already is.
 */

import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import OrgInviteBanner from "@/components/OrgInviteBanner";
import AccountSection from "@/components/settings/AccountSection";
import NotificationsSection from "@/components/settings/NotificationsSection";
import PlanSection from "@/components/settings/PlanSection";
import ProfileSection from "@/components/settings/ProfileSection";
import ShareSection from "@/components/settings/ShareSection";
import SchedulingSection from "@/components/settings/SchedulingSection";
import CalendarSyncSection from "@/components/settings/CalendarSyncSection";
import StripeCheckout from "@/components/StripeCheckout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuthContext } from "@/App";
import api from "@/lib/api";
import { companyName } from "@/lib/companyBrand";
import { calendarSyncFlags, subscriptionsEnabled } from "@/lib/featureFlags";
import { accountTier, bestOrgSeat, isPaidTier } from "@/lib/tiers";
import useUsage from "@/hooks/useUsage";

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setUser } = useContext(AuthContext);
  // Server-resolved, not the build-time constant: an ops-console flip has to
  // move the paywall UI and the quota that backs it in the same breath.
  const subsEnabled = subscriptionsEnabled(user);
  const { usage, loading: usageLoading } = useUsage(subsEnabled);
  const [referralStats, setReferralStats] = useState(null);
  // `/subscription/status`: the effective tier, the covering seat and X7's
  // `{tier, until}` pin, all resolved SERVER-side. Whether a pin counts turns
  // on four rules (undated does nothing, expired is ignored, unknown value is
  // ignored, it raises but never lowers) and re-implementing them here is how
  // the two answers come to disagree.
  const [status, setStatus] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  // Reported by StripeCheckout so the popup can shrink to the payment
  // form. Reset on close, or reopening would start narrow and jump.
  const [checkoutStep, setCheckoutStep] = useState("select");
  const [showTopUp, setShowTopUp] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const ignoreAbort = (err) => {
      if (!controller.signal.aborted) console.error(err);
    };
    // A 404 is the REFERRAL_ENABLED kill switch, not a failure: stats stay
    // null, the row stays hidden, and nothing is logged.
    api
      .get("/referral/stats", { signal: controller.signal })
      .then((res) => setReferralStats(res.data))
      .catch((err) => {
        if (err?.response?.status !== 404) ignoreAbort(err);
      });
    // Fetched whenever the plan row is rendered at all — including for an
    // account whose stored tier is free, since a pin is exactly what makes it
    // not free.
    if (subsEnabled) {
      api
        .get("/subscription/status", { signal: controller.signal })
        .then((res) => setStatus(res.data || null))
        .catch(ignoreAbort);
    }
    return () => controller.abort();
  }, [subsEnabled]);

  // The status endpoint's tier is already seat-aware (it reports the seat's
  // tier when the seat is what covers the account). Before it lands, the
  // /auth/me copy carries the same pin-aware answer, and the seat rule is
  // mirrored here so the first paint agrees with the second: a seat names the
  // plan only when the personal tier is free — otherwise the account is
  // paying for its own, and the seat is reported beside it.
  const orgSeat = status?.org_seat || bestOrgSeat(user);
  const tier = status?.subscription_tier || accountTier(user);
  // An org seat covers this account — no upsell (WP5). A personal purchase
  // stays possible from the pricing page if they want to outrank the seat.
  const canUpgrade = subsEnabled && !isPaidTier(tier) && !orgSeat;

  // Deep link from the mobile paywall's "subscribe on our website" link-out:
  // /settings?upgrade=1 opens the Stripe checkout directly for accounts that
  // can actually buy (same condition as the Upgrade button). The param is
  // consumed either way so a reload doesn't reopen checkout.
  useEffect(() => {
    if (searchParams.get("upgrade") !== "1") return;
    if (canUpgrade) setShowCheckout(true);
    const next = new URLSearchParams(searchParams);
    next.delete("upgrade");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canUpgrade]);

  // /settings?topup=1 — the same idiom for minutes (I6). The recording screen
  // links here when a lesson is refused for want of minutes, which is the one
  // moment someone actually wants to buy them.
  useEffect(() => {
    if (searchParams.get("topup") !== "1") return;
    if (subsEnabled) setShowTopUp(true);
    const next = new URLSearchParams(searchParams);
    next.delete("topup");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, subsEnabled]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
      toast.success("Logged out");
      navigate("/");
    } catch (_error) {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--studio-base)]">
      <main className="mx-auto max-w-[900px] px-6 pb-32 pt-11 md:px-12" data-testid="settings-page">
        <div className="mb-8">
          <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Preferences</p>
          <h1 className="mt-1.5 font-serif text-[40px] font-normal leading-none tracking-[-0.02em] text-[var(--text-primary)]">Settings</h1>
        </div>

        <div className="flex flex-col gap-9">
          <OrgInviteBanner />

          <ProfileSection user={user} setUser={setUser} tier={tier} />

          <SchedulingSection />

          <CalendarSyncSection portal="instructor" client={api} prefix="" available={calendarSyncFlags(user)} />

          <PlanSection
            user={user}
            tier={tier}
            orgSeat={orgSeat}
            grandfathered={status?.grandfathered || null}
            subsEnabled={subsEnabled}
            usage={usage}
            usageLoading={usageLoading}
            showTopUp={showTopUp}
            onOpenTopUp={() => setShowTopUp(true)}
            onCloseTopUp={() => setShowTopUp(false)}
            canUpgrade={canUpgrade}
            onUpgrade={() => setShowCheckout(true)}
          />

          <ShareSection referralStats={referralStats} />

          <NotificationsSection />

          <AccountSection onConsentLedger={() => navigate("/consent-ledger")} onLogout={handleLogout} />

          <p className="text-center font-mono text-[10px] tracking-widest text-[var(--text-quaternary)]">by {companyName()}</p>
        </div>

        {/* The ladder, on demand. A dialog rather than an in-card step:
            choosing a plan is a decision of its own, and the page behind it is
            not part of it. The dialog owns the title and the close control, so
            the checkout body draws no header of its own. */}
        <Dialog
          open={showCheckout}
          onOpenChange={(open) => {
            setShowCheckout(open);
            if (!open) setCheckoutStep("select");
          }}
        >
          {/* The popup is sized to the STEP, not to the widest thing it will
              ever hold. The plan grid needs 960px for the two tiles to sit side
              by side with every bullet on one line — at the old 36rem they were
              squeezed into narrow columns of wrapped text, and a comparison
              that wraps every line is not a comparison. Stripe's payment form,
              though, is a ~400px centred column, so at 960 it sat in the middle
              of two thirds of empty white. */}
          <DialogContent
            className={`bg-[var(--studio-raised)] border-[var(--studio-divider)] w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto p-6 transition-[max-width] duration-200 md:p-8 ${
              checkoutStep === "select" ? "sm:max-w-[960px]" : "sm:max-w-[540px]"
            }`}
            data-testid="upgrade-dialog"
          >
            <DialogHeader>
              <DialogTitle className="font-serif text-[var(--text-primary)]">Upgrade your plan</DialogTitle>
            </DialogHeader>
            {/* Mounted only while open, so reopening starts on the plan tiles
                rather than wherever the last visit was abandoned — and no
                Stripe session is created behind a closed dialog. */}
            {showCheckout && <StripeCheckout onStepChange={setCheckoutStep} />}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SettingsPage;
